import React, { useEffect } from "react";

import Section from "../../Controls/Sections";
import $ from "jquery";
// @ts-ignore
import html2pdf from "html2pdf.js";
import {
  Box,
  Divider,
  Heading,
  HStack,
  List,
  ListItem,
  Text,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { ActionModal } from "../../Controls/Modal";
import { formatDate } from "../../../utils/formatDate";
import {
  Consent,
  DataHolderBrand,
  SoftwareProductWording,
} from "../../../Types/AppData";

const Row = ({
  label,
  children,
  showLabelColon = true,
}: {
  label: string;
  children?: string | null | any;
  showLabelColon?: boolean;
}) => {
  return (
    <HStack spacing={"2px"}>
      <Text fontWeight="bold">
        {label}
        {showLabelColon ? ": " : ""}
      </Text>
      <Text>{children}</Text>
    </HStack>
  );
};

const Subsection = ({
  children,
  showBorderBottom = true,
}: {
  children: React.ReactNode;
  showBorderBottom?: boolean;
}) => {
  return (
    <>
      <VStack alignItems="baseline" spacing={"6px"}>
        {children}
      </VStack>
      {showBorderBottom && <Divider my={6} />}
    </>
  );
};

interface ConfirmationOfConsentProps {
  handleClose: () => void;
  showPopup?: boolean;
  consent?: Consent;
  dataHolderBrand?: DataHolderBrand;
  softwareProductWording?: SoftwareProductWording;
}

export default function ConfirmationOfConsent({
  showPopup,
  handleClose,
  consent,
  dataHolderBrand,
  softwareProductWording,
}: ConfirmationOfConsentProps) {
  const toast = useToast();

  let startTime = formatDate(consent?.startTime);
  let endTime = formatDate(consent?.endTime);
  let withdrawnTime = formatDate(consent?.withdrawnTime ?? "");

  const hasAdditionalUses =
    consent?.additionalUses?.length > 0 &&
    !(
      consent?.additionalUses?.length === 1 && consent?.additionalUses[0] === ""
    );

  const hasAccounts =
    Boolean(consent?.accounts) && consent!.accounts!.length > 0;

  function SaveAsPdf(event: any) {
    event.preventDefault();

    let printContent = $("#contentToPrint").html();

    let opt = {
      margin: 1,
      filename: "Confirmation Of Consent.pdf",
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
    };

    html2pdf().set(opt).from(printContent).save();

    toast({
      title: "Your document was downloaded successfully.",
      status: "success",
      duration: 4000,
      isClosable: true,
      position: "top",
    });
  }

  function StatusCheck(component: any) {
    const knownStatuses = ["active", "pending", "withdrawn", "expired"];

    const contentStatus = consent?.status.toLowerCase() ?? "";

    return knownStatuses.includes(contentStatus) ? component : null;
  }

  function content() {
    return (
      <div>
        <Section>
          {/* Key dates */}
          <Box id="contentToPrint">
            <Subsection>
              <Heading fontSize="lg" mb={3}>
                CDR Receipt
              </Heading>

              <Row label="Consent ID">{consent?.id}</Row>

              <Row label="Consent Type">
                {consent?.isOneOffConsent ? "Once-off" : "Ongoing"}
              </Row>

              <Row label="Consent Status">{consent?.status}</Row>

              <Row label="Data Holder Legal Entity">
                {dataHolderBrand?.legalEntity?.legalEntityName}
              </Row>

              <Row label="Data Holder Brand">{dataHolderBrand?.brandName}</Row>

              <Row label="Data Access Frequency">
                {softwareProductWording?.paragraphHowOftenDataAccessed}
              </Row>

              {StatusCheck(
                <Row label="CDR Arrangement Id">
                  {consent?.cdrArrangementId}
                </Row>
              )}
            </Subsection>

            {/* Key dates */}
            <Subsection>
              <Heading fontSize="lg">Key dates</Heading>

              <Row label="When your consent was created">{startTime}</Row>

              {StatusCheck(
                <>
                  <Row label="When your consent will expire">{endTime}</Row>
                </>
              )}

              <Row label="Sharing period">
                {startTime} - {endTime}
              </Row>

              {!consent?.isOneOffConsent && !!withdrawnTime ? (
                <>
                  <Row label="When your consent was withdrawn">
                    {withdrawnTime}
                  </Row>
                </>
              ) : null}
            </Subsection>

            <Heading fontSize="lg" mb={2}>
              Data Requested
            </Heading>

            {/* Consents */}

            {/* Individual */}
            {consent?.customerType === "Individual" ? (
              <>
                {/* Name and occupation */}
                {consent?.nameAndOccupationConsent !== "NotRequested" &&
                consent?.nameOccupationContactDetailsConsent ===
                  "NotRequested" ? (
                  <Subsection>
                    <Heading fontSize="md">Name and occupation</Heading>

                    <List>
                      <ListItem>Name</ListItem>
                      <ListItem>Occupation</ListItem>
                    </List>

                    <Heading fontSize="md">Why we need it</Heading>
                    <Text>
                      {softwareProductWording?.whyWeNeedNameAndOccupation}
                    </Text>

                    {/* <Heading fontSize='md'>Consented</Heading> */}
                    <Row label="Consented">
                      {consent?.nameAndOccupationConsent ===
                      "RequestedAndConsented"
                        ? "Yes"
                        : "No"}
                    </Row>
                  </Subsection>
                ) : null}

                {/* Contact details  */}
                {consent?.contactDetailsConsent !== "NotRequested" &&
                consent?.nameOccupationContactDetailsConsent ===
                  "NotRequested" ? (
                  <Subsection>
                    <Heading fontSize="md">Contact details</Heading>

                    <List>
                      <ListItem>Phone</ListItem>
                      <ListItem>Email address</ListItem>
                      <ListItem>Mail address</ListItem>
                      <ListItem>Residential address</ListItem>
                    </List>

                    <Heading fontSize="md">Why we need it</Heading>
                    <Text>
                      {softwareProductWording?.whyWeNeedContactDetails}
                    </Text>

                    {/* <Heading fontSize='md'>Consented</Heading> */}
                    <Text>
                      <b>Consented:</b>{" "}
                      {consent?.contactDetailsConsent ===
                      "RequestedAndConsented"
                        ? "Yes"
                        : "No"}
                    </Text>
                  </Subsection>
                ) : null}

                {/* Name, occupation and contact details  */}
                {consent?.nameOccupationContactDetailsConsent !==
                "NotRequested" ? (
                  <Subsection>
                    <Heading fontSize="md">
                      Name, occupation and contact details
                    </Heading>

                    <List>
                      <ListItem>Name</ListItem>
                      <ListItem>Occupation</ListItem>
                      <ListItem>Phone</ListItem>
                      <ListItem>Email address</ListItem>
                      <ListItem>Mail address</ListItem>
                      <ListItem>Residential address</ListItem>
                    </List>

                    <Heading fontSize="md">Why we need it</Heading>
                    <Text>
                      {
                        softwareProductWording?.whyWeNeedNameOccupationContactDetails
                      }
                    </Text>

                    {/* <Heading fontSize='md'>Consented</Heading> */}
                    <Row label="Consented">
                      {consent?.nameOccupationContactDetailsConsent ===
                        "RequestedAndConsented" ||
                      consent?.nameOccupationContactDetailsConsent ===
                        "OptionalAndConsented"
                        ? "Yes"
                        : "No"}
                    </Row>
                  </Subsection>
                ) : null}
              </>
            ) : null}

            {/* Business */}
            {consent?.customerType === "Business" ? (
              <>
                {/* Organisation profile   */}
                {consent?.organisationProfileConsent !== "NotRequested" &&
                consent?.organisationProfileAndContactDetailsConsent ===
                  "NotRequested" ? (
                  <Subsection>
                    <Heading fontSize="md">Organisation profile</Heading>

                    <List>
                      <ListItem>Agent name and role</ListItem>
                      <ListItem>Organisation name</ListItem>
                      <ListItem>Organisation numbers (ABN or ACN)</ListItem>
                      <ListItem>Charity status</ListItem>
                      <ListItem>Establishment date</ListItem>
                      <ListItem>Industry</ListItem>
                      <ListItem>Organisation type</ListItem>
                      <ListItem>Country of registration</ListItem>
                    </List>

                    <Heading fontSize="md">Why we need it</Heading>
                    <Text>
                      {softwareProductWording?.whyWeNeedOrganisationProfile}
                    </Text>

                    <Row label="Consented">
                      {consent?.organisationProfileConsent ===
                      "RequestedAndConsented"
                        ? "Yes"
                        : "No"}
                    </Row>
                  </Subsection>
                ) : null}

                {/* Organisation contact details  */}
                {consent?.organisationContactDetailsConsent !==
                  "NotRequested" &&
                consent?.organisationProfileAndContactDetailsConsent ===
                  "NotRequested" ? (
                  <Subsection>
                    <Heading fontSize="md">
                      Organisation contact details
                    </Heading>

                    <List>
                      <ListItem>Organisation address</ListItem>
                      <ListItem>Mail address</ListItem>
                      <ListItem>Phone number</ListItem>
                    </List>

                    <Heading fontSize="md">Why we need it</Heading>
                    <Text>
                      {
                        softwareProductWording?.whyWeNeedOrganisationContactDetails
                      }
                    </Text>

                    <Row label="Consented">
                      {consent?.organisationContactDetailsConsent ===
                      "RequestedAndConsented"
                        ? "Yes"
                        : "No"}
                    </Row>
                  </Subsection>
                ) : null}

                {/* Organisation profile and contact details  */}
                {consent?.organisationProfileAndContactDetailsConsent !==
                "NotRequested" ? (
                  <Subsection>
                    <Heading fontSize="md">
                      Organisation profile and contact details
                    </Heading>

                    <List>
                      <ListItem>Agent name and role</ListItem>
                      <ListItem>Organisation name</ListItem>
                      <ListItem>Organisation numbers (ABN or ACN)</ListItem>
                      <ListItem>Charity status</ListItem>
                      <ListItem>Establishment date</ListItem>
                      <ListItem>Industry</ListItem>
                      <ListItem>Organisation type</ListItem>
                      <ListItem>Country of registration</ListItem>
                      <ListItem>Organisation address</ListItem>
                      <ListItem>Mail address</ListItem>
                      <ListItem>Phone number</ListItem>
                    </List>

                    <Heading fontSize="md">Why we need it</Heading>
                    <p>
                      {
                        softwareProductWording?.whyWeNeedOrganisationProfileAndContactDetails
                      }
                    </p>

                    {/* <Heading fontSize='md'>Consented</Heading> */}
                    <p>
                      <b>Consented:</b>{" "}
                      {consent?.organisationProfileAndContactDetailsConsent ===
                      "RequestedAndConsented"
                        ? "Yes"
                        : "No"}
                    </p>
                  </Subsection>
                ) : null}
              </>
            ) : null}

            {/* Account name, type and balance  */}
            {consent?.accountNameTypeAndBalanceConsent !== "NotRequested" &&
            consent?.accountBalanceAndDetailsConsent === "NotRequested" ? (
              <Subsection>
                <Heading fontSize="md">Account name, type and balance</Heading>

                <List>
                  <ListItem>Name of account</ListItem>
                  <ListItem>Type of account</ListItem>
                  <ListItem>Account balance</ListItem>
                </List>

                <Heading fontSize="md">Why we need it</Heading>
                <p>{softwareProductWording?.whyWeNeedTransactionDetails}</p>

                {/* <Heading fontSize='md'>Consented</Heading> */}
                <p>
                  <b>Consented:</b>{" "}
                  {consent?.accountNameTypeAndBalanceConsent ===
                  "RequestedAndConsented"
                    ? "Yes"
                    : "No"}
                </p>
              </Subsection>
            ) : null}

            {/* Account numbers and features  */}
            {consent?.accountNumbersAndFeaturesConsent !== "NotRequested" &&
            consent?.accountBalanceAndDetailsConsent === "NotRequested" ? (
              <Subsection>
                <Heading fontSize="md">Account numbers and features</Heading>

                <List>
                  <ListItem>Account number</ListItem>
                  <ListItem>Interest rates</ListItem>
                  <ListItem>Fees</ListItem>
                  <ListItem>Discounts</ListItem>
                  <ListItem>Account terms</ListItem>
                  <ListItem>Account mail address</ListItem>
                </List>

                <Heading fontSize="md">Why we need it</Heading>
                <p>
                  {softwareProductWording?.whyWeNeedAccountNumbersAndFeatures}
                </p>

                {/* <Heading fontSize='md'>Consented</Heading> */}
                <p>
                  <b>Consented:</b>{" "}
                  {consent?.accountNumbersAndFeaturesConsent ===
                  "RequestedAndConsented"
                    ? "Yes"
                    : "No"}
                </p>
              </Subsection>
            ) : null}

            {/* Account balance and details */}
            {consent?.accountBalanceAndDetailsConsent !== "NotRequested" ? (
              <Subsection>
                <Heading fontSize="md">Account balance and details</Heading>

                <List>
                  <ListItem>Name of account</ListItem>
                  <ListItem>Type of account</ListItem>
                  <ListItem>Account balance</ListItem>
                  <ListItem>Account number</ListItem>
                  <ListItem>Interest rates</ListItem>
                  <ListItem>Fees</ListItem>
                  <ListItem>Discounts</ListItem>
                  <ListItem>Account terms</ListItem>
                  <ListItem>Account mail address</ListItem>
                </List>

                <Heading fontSize="md">Why we need it</Heading>
                <p>
                  {softwareProductWording?.whyWeNeedAccountBalanceAndDetails}
                </p>

                {/* <Heading fontSize='md'>Consented</Heading> */}
                <p>
                  <b>Consented:</b>{" "}
                  {consent?.accountBalanceAndDetailsConsent ===
                    "RequestedAndConsented" ||
                  consent?.accountBalanceAndDetailsConsent ===
                    "RequiredAndConsented"
                    ? "Yes"
                    : "No"}
                </p>
              </Subsection>
            ) : null}

            {/* Transaction details */}
            {consent?.transactionDetailsConsent !== "NotRequested" ? (
              <Subsection>
                <Heading fontSize="md">Transaction details</Heading>

                <List>
                  <ListItem>Incoming and outgoing transactions</ListItem>
                  <ListItem>Amounts</ListItem>
                  <ListItem>Dates</ListItem>
                  <ListItem>Descriptions of transactions</ListItem>
                  <ListItem>
                    Who you've sent money to and recieved from (e.g. their name)
                  </ListItem>
                </List>

                <Heading fontSize="md">Why we need it</Heading>
                <p>{softwareProductWording?.whyWeNeedTransactionDetails}</p>

                {/* <Heading fontSize='md'>Consented</Heading> */}
                <p>
                  <b>Consented:</b>{" "}
                  {consent?.transactionDetailsConsent ===
                    "RequestedAndConsented" ||
                  consent?.transactionDetailsConsent === "RequiredAndConsented"
                    ? "Yes"
                    : "No"}
                </p>
              </Subsection>
            ) : null}

            {/* Direct debits and scheduled payments */}
            {consent?.directDebitsAndScheduledPaymentsConsent !==
            "NotRequested" ? (
              <Subsection>
                <Heading fontSize="md">
                  Direct debits and scheduled payments
                </Heading>

                <List>
                  <ListItem>Direct debits</ListItem>
                  <ListItem>Scheduled payments</ListItem>
                </List>

                <Heading fontSize="md">Why we need it</Heading>
                <p>
                  {
                    softwareProductWording?.whyWeNeedDirectDebitsAndScheduledPayments
                  }
                </p>

                {/* <Heading fontSize='md'>Consented</Heading> */}
                <p>
                  <b>Consented:</b>{" "}
                  {consent?.directDebitsAndScheduledPaymentsConsent ===
                  "RequestedAndConsented"
                    ? "Yes"
                    : "No"}
                </p>
              </Subsection>
            ) : null}

            {/* Saved payees */}
            {consent?.savedPayeesConsent !== "NotRequested" ? (
              <Subsection>
                <Heading fontSize="md">Saved payees</Heading>

                <List>
                  <ListItem>
                    Names and details of accounts you have saved (e.g. their BSB
                    and Account Number, BPay CRN and Biller code, or NPP PayID)
                  </ListItem>
                </List>

                <Heading fontSize="md">Why we need it</Heading>
                <p>{softwareProductWording?.whyWeNeedSavedPayees}</p>

                {/* <Heading fontSize='md'>Consented</Heading> */}
                <p>
                  <b>Consented:</b>{" "}
                  {consent?.savedPayeesConsent === "RequestedAndConsented"
                    ? "Yes"
                    : "No"}
                </p>
              </Subsection>
            ) : null}

            {/* Accounts */}
            {hasAccounts ? (
              <Subsection>
                <Heading fontSize="md">Accounts</Heading>
                {consent?.accounts?.map((a: any, index: any) => (
                  <div key={index}>
                    <Heading fontSize="sm">{a?.name}</Heading>
                    <Text>{a?.maskedAccountNumber}</Text>
                  </div>
                ))}
              </Subsection>
            ) : null}

            {/* Additional uses of your data */}
            {hasAdditionalUses ? (
              <Subsection>
                <Heading fontSize="md">Additional uses of your data</Heading>
                {softwareProductWording?.softwareProductWordingAdditionalUses?.map(
                  (au: any) => (
                    <Box key={au.id}>
                      <Heading fontSize="sm">{au?.description}</Heading>
                      <Row label="Consented">
                        {consent?.additionalUses.includes(au.id)
                          ? " Yes"
                          : " No"}
                      </Row>
                    </Box>
                  )
                )}
              </Subsection>
            ) : null}

            {/* Supporting third parties */}
            {Boolean(
              (softwareProductWording?.softwareProductWordingThirdParties || [])
                .length > 0
            ) ? (
              <Subsection>
                <Heading fontSize="md">Supporting third parties</Heading>
                {softwareProductWording?.softwareProductWordingThirdParties?.map(
                  (tp: any) => (
                    <Row key={tp.id} label={tp.name}>
                      {tp.description}
                    </Row>
                  )
                )}
              </Subsection>
            ) : null}

            {/* Treatment of Redundant Data */}
            <Subsection>
              <Heading fontSize="md">Treatment of Redundant Data</Heading>

              {/* De-identification of data */}
              {softwareProductWording?.canDeIdentifyData ? (
                <>
                  <Row label="De-identification of data">
                    {softwareProductWording?.canDeIdentifyData ? " Yes" : " No"}
                  </Row>
                  <Text>
                    {softwareProductWording?.canDeIdentifyData
                      ? softwareProductWording?.paragraphDeIdentification
                      : null}
                  </Text>

                  <Row label="Requested to delete my data instead">
                    {consent?.deleteMyData ? " Yes" : " No"}
                  </Row>
                  <Text>
                    {consent?.deleteMyData
                      ? softwareProductWording?.paragraphDataDeletion
                      : null}
                  </Text>
                </>
              ) : (
                <>
                  <Text fontWeight="bold">Your data will be deleted</Text>
                  <Text>{softwareProductWording?.paragraphDataDeletion}</Text>
                </>
              )}
            </Subsection>
          </Box>
        </Section>
      </div>
    );
  }

  return (
    <ActionModal
      size={"2xl"}
      show={Boolean(showPopup)}
      handleOnClose={() => handleClose()}
      title="Confirmation Of Consent"
      confirmButtonText="Download as PDF"
      handleAction={(e) => SaveAsPdf(e)}
      cancelButtonText="Close"
    >
      {content()}
    </ActionModal>
  );
}
